import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "react-oidc-context";
import { WebStorageStateStore } from "oidc-client-ts";
import { datadogRum } from "@datadog/browser-rum";

import AuthError from "./components/AuthError";
import AuthLoading from "./components/AuthLoading";
import AxiosInterceptor from "./lib/axiosInterceptor";
import useWindowInFocus from "../../hooks/useWindowInFocus";
import NitroService from "../../utils/nitro.service";

const AuthChild = ({
  loadingStyle,
  children,
}: {
  loadingStyle: {
    color: string;
    backgroundColor: string;
    height?: string | number;
    width?: string | number;
  };
  children: React.ReactNode;
}) => {
  const auth = useAuth();
  const authenticatedUser = auth.user;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const userId = authenticatedUser?.profile.preferred_username;
    NitroService.setAuthenticatedUserId(
      typeof userId === "string" ? userId : undefined,
    );
  }, [authenticatedUser]);

  useWindowInFocus({
    onFocus: () => {
      if (auth.user?.expired) {
        auth.signinRedirect();
      }
    },
  });

  useEffect(() => {
    setIsLoading(true);
  }, []);

  const [isComponentMounted, setIsComponentMounted] = useState(false);
  useEffect(() => {
    setIsComponentMounted(true);
  }, []);

  useEffect(() => {
    if (!isComponentMounted) return;
    console.log(
      `KAESAuth: ${window.location.pathname}${window.location.search}`,
      {
        isLoading: auth.isLoading,
        isAuthenticated: auth.isAuthenticated,
        user: auth.user,
      }
    );

    datadogRum.setUser({
      name: auth.user?.profile.name,
    });

    const now = Number(new Date().valueOf().toString().slice(0, -3));
    const isExpired = auth.user?.expires_at && now > auth.user?.expires_at;

    if (auth.user?.access_token) {
      AxiosInterceptor.create(auth.user.access_token, () => {
        auth.signoutRedirect();
        auth.clearStaleState();
      });
      setIsLoading(false);
      if (
        !auth.isLoading &&
        auth.isAuthenticated &&
        window.location.pathname === "/login"
      ) {
        console.log("KAESAuth-Redirect to /", { user: auth.user });
        navigate("/");
      }
    }

    if (!auth.isLoading && !auth.isAuthenticated && (!auth.user || isExpired)) {
      auth.signinRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, isComponentMounted, auth]);

  if (window.location.pathname === "/error") {
    return <AuthError />;
  }

  if (
    isLoading ||
    auth.isLoading ||
    !auth.isAuthenticated ||
    !auth.user ||
    window.location.pathname === "/login"
  ) {
    return (
      <AuthLoading
        color={loadingStyle.color}
        backgroundColor={loadingStyle.backgroundColor}
        height={loadingStyle.height}
        width={loadingStyle.width}
      />
    );
  }

  return <div>{children}</div>;
};

const KAESAuth = ({
  client_id,
  authority,
  redirect_uri,
  silent_redirect_uri,
  metadata,
  storagePrefix,
  loadingStyle,
  children,
}: {
  client_id: string;
  authority: string;
  redirect_uri: string;
  silent_redirect_uri: string;
  metadata: {
    authorization_endpoint: string;
    token_endpoint: string;
    revocation_endpoint: string;
    userinfo_endpoint: string;
    end_session_endpoint?: string;
  };
  storagePrefix: string;
  loadingStyle: {
    color: string;
    backgroundColor: string;
    height?: string | number;
    width?: string | number;
  };
  children: React.ReactNode;
}) => {
  return (
    <AuthProvider
      client_id={client_id}
      authority={authority}
      redirect_uri={redirect_uri}
      scope="openid email profile aws.cognito.signin.user.admin"
      response_type="code"
      automaticSilentRenew
      silent_redirect_uri={silent_redirect_uri}
      metadata={metadata}
      loadUserInfo
      userStore={
        new WebStorageStateStore({
          prefix: `${storagePrefix}.`,
          store: window.localStorage,
        })
      }
    >
      <AuthChild loadingStyle={loadingStyle}>{children}</AuthChild>
    </AuthProvider>
  );
};

export default KAESAuth;
